import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Auth } from 'aws-amplify';
import { useRequest } from 'ahooks';
import axios from 'axios';
import { Alert, Button, message, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { saveAs } from 'file-saver';
import IframeResizer from 'iframe-resizer-react';
import { UserContext } from '../contexts/UserContext';

const ClientSubmissionPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const [downloading, setDownloading] = useState(false);
  const { data, loading, error, cancel } = useRequest(
    async () => {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      const response = await axios.get(
        `${process.env.REACT_APP_CHALICE_URL}/submission-dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Hasura-Role': user?.role,
          },
        }
      );
      return response.data;
    },
    {
      pollingInterval: 1000 * 60 * 59,
    }
  );

  useEffect(() => cancel, [cancel]);

  const downloadSubmissionFiles = async () => {
    setDownloading(true);
    try {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      const response = await axios.get(
        `${process.env.REACT_APP_CHALICE_URL}/submission-files`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Hasura-Role': user?.role,
            Accept: 'application/zip',
          },
          responseType: 'blob',
        }
      );
      const disposition = response.headers['content-disposition'] || '';
      const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const filename = encodedFilename
        ? decodeURIComponent(encodedFilename[1])
        : disposition.match(/filename="([^"]+)"/i)?.[1] || 'Submission.zip';
      saveAs(response.data, filename);
    } catch (error) {
      let errorMessage =
        'Unable to download submission files. Please try again.';
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text());
          if (body.message) errorMessage = body.message;
        } catch {
          // Keep the fallback for errors without a JSON response.
        }
      }
      message.error(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Submission | Mabel</title>
      </Helmet>
      <Typography.Title level={2} style={{ marginBottom: 4 }}>
        Submission
      </Typography.Title>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          loading={downloading}
          onClick={downloadSubmissionFiles}
        >
          Download Submission Files
        </Button>
      </div>
      <Spin spinning={loading} size="large">
        {error && (
          <Alert
            type="error"
            showIcon
            message="Unable to load the submission dashboard. Please try again."
          />
        )}
        {data?.dashboards?.[0]?.url && (
          <IframeResizer
            title="Submission dashboard"
            src={data.dashboards[0].url}
            style={{ width: '1px', minWidth: '100%' }}
            frameBorder={0}
            allowTransparency
          />
        )}
      </Spin>
    </>
  );
};

export default ClientSubmissionPage;
